'use client'

import { AlertTriangle, Info, XCircle } from 'lucide-react'
import { InstrumentLabel, Panel } from './ui'

/**
 * Errors / warnings / notices at a glance, with a footer that says what
 * changed since the last scan.
 *
 * Each row carries an icon as well as a colour — severity has to survive
 * without colour perception.
 */

interface Severity {
  label: string
  count: number
  color: string
  icon: typeof XCircle
}

export function IssuesSummaryCard({
  errors,
  warnings,
  notices,
  previousIssueCount,
  onSelect,
}: {
  errors: number
  warnings: number
  notices: number
  previousIssueCount?: number | null
  /** Opens the Actions tab filtered to a severity. */
  onSelect?: (severity: 'errors' | 'warnings' | 'notices') => void
}) {
  const total = errors + warnings + notices
  const severities: (Severity & { id: 'errors' | 'warnings' | 'notices' })[] = [
    {
      id: 'errors',
      label: 'Errors',
      count: errors,
      color: 'var(--cta)',
      icon: XCircle,
    },
    {
      id: 'warnings',
      label: 'Warnings',
      count: warnings,
      color: 'var(--warning-text)',
      icon: AlertTriangle,
    },
    {
      id: 'notices',
      label: 'Notices',
      count: notices,
      color: 'var(--primary)',
      icon: Info,
    },
  ]

  // Denominator for the bars: the largest single severity, so a lone category
  // still reads as full rather than a third of the track.
  const scale = Math.max(errors, warnings, notices, 1)

  return (
    <Panel className='flex flex-col gap-4 p-6 sm:p-7'>
      <InstrumentLabel tone='secondary'>Issues · {total} open</InstrumentLabel>

      <div className='flex flex-col gap-3'>
        {severities.map(s => {
          const Icon = s.icon
          const pct = (s.count / scale) * 100
          const row = (
            <>
              <div className='flex items-baseline justify-between gap-2 text-[13px]'>
                <span className='flex items-center gap-1.5 text-muted-foreground'>
                  <Icon
                    size={12}
                    aria-hidden='true'
                    className='shrink-0 self-center'
                    style={{
                      color: s.count > 0 ? s.color : 'var(--pm-text-muted)',
                    }}
                  />
                  {s.label}
                </span>
                <span
                  className='font-mono tabular-nums'
                  style={{
                    color: s.count > 0 ? s.color : 'var(--pm-text-muted)',
                  }}
                >
                  {s.count}
                </span>
              </div>
              <div className='h-1 w-full bg-surface-inset'>
                {s.count > 0 && (
                  <div
                    className='h-1 transition-[width] duration-500'
                    style={{ width: `${pct}%`, background: s.color }}
                  />
                )}
              </div>
            </>
          )

          if (!onSelect || s.count === 0) {
            return (
              <div key={s.id} className='flex flex-col gap-1.5'>
                {row}
              </div>
            )
          }

          return (
            <button
              key={s.id}
              type='button'
              onClick={() => onSelect(s.id)}
              aria-label={`${s.count} ${s.label.toLowerCase()}. Open the actions list.`}
              className='flex flex-col gap-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
            >
              {row}
            </button>
          )
        })}
      </div>

      <span className='block mt-auto border-t border-hairline pt-3 text-[13px] text-instrument'>
        {errors > 0
          ? `${errors} critical ${errors === 1 ? 'issue' : 'issues'}.`
          : 'No critical issues.'}{' '}
        {previousIssueCount === null || previousIssueCount === undefined
          ? 'No previous scan to compare.'
          : total === previousIssueCount
            ? 'No change since last scan.'
            : total < previousIssueCount
              ? `Down ${previousIssueCount - total} since last scan.`
              : `Up ${total - previousIssueCount} since last scan.`}
      </span>
    </Panel>
  )
}
